/*
 * © 2026 Ecsper Ltd. All rights reserved.
 * SPDX-License-Identifier: LicenseRef-Ecsper-Proprietary
 * Unauthorised use, copying, or distribution is strictly prohibited.
 */

import '../styles/tokens.css';
import '../styles/sidebar.css';
import '@ecsper/ui-panels/panels.css';
import '../commands.js';
import '../commands/commands.js';

import { ToastProvider } from '@ecsper/ui-primitives';
import { type ReactElement, useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';

import { type AppContext, bootstrap } from '../bootstrap.js';
import { AuthGateTree } from './AuthGate.js';
import { LoadingPane } from './LoadingPane.js';
import { resolveReportContext } from './resolveReportContext.js';

function showFatalAlert(message: string): void {
  const alertEl = document.createElement('div');
  alertEl.setAttribute('role', 'alert');
  alertEl.textContent = message;
  document.body.replaceChildren(alertEl);
}

function TaskPaneRoot(props: { readonly ctx: AppContext }): ReactElement {
  const { ctx } = props;
  const [reportContext, setReportContext] = useState<Awaited<
    ReturnType<typeof resolveReportContext>
  > | null>(null);
  const [reportId, setReportId] = useState<string | null>(null);

  useEffect(() => {
    void resolveReportContext().then((resolved) => {
      // eslint-disable-next-line no-console -- binding source diagnostics for DevTools
      console.info(`[Ecsper] Report context resolved from ${resolved.source}`, {
        reportId: resolved.reportId,
        matterId: resolved.matterId,
      });
      setReportContext(resolved);
      setReportId(resolved.reportId ?? null);
    });
  }, []);

  if (reportContext === null) {
    return <LoadingPane />;
  }

  return (
    <ToastProvider>
      <AuthGateTree
        ctx={ctx}
        reportId={reportId}
        matterId={reportContext.matterId ?? null}
        onReportSelected={(id) => {
          setReportId(id);
        }}
      />
    </ToastProvider>
  );
}

async function main(): Promise<void> {
  await new Promise<void>((resolve) => {
    Office.onReady(() => {
      resolve();
    });
  });

  const ctx = await bootstrap();

  const rootEl = document.getElementById('root');
  if (rootEl === null) {
    showFatalAlert('Missing root element.');
    return;
  }

  const root = createRoot(rootEl);
  root.render(<TaskPaneRoot ctx={ctx} />);
}

void main().catch((e: unknown) => {
  // eslint-disable-next-line no-console -- bootstrap failure diagnostics
  console.error('Bootstrap failed:', e);
  const message = e instanceof Error ? e.message : String(e);
  showFatalAlert(`Bootstrap failed: ${message}`);
});
